import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Chart from 'react-apexcharts';
import Loader from '../../../../component/common/Loader';
import { minsToHhMm } from '../../../SleepHub/sleepHubApi';
import { fetchCompanyPodsUserDetail } from './companyPodsApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  PANEL_ROW_CLASS,
  Chip,
  EntityHeader,
  BackLink,
  STAT_ICONS,
  CHART_BASE,
  CHART_GRID_NO_X,
  toneChip,
  paletteAt
} from '../../../../component/common/designSystem';
import { formatChipClass } from './podsTheme';

/**
 * Shoorah Pods → User profile.
 * One user's listening: totals, the pods they return to, and their most recent
 * plays. `fetcher`/`backPath`/`backLabel` are props so a future super-admin
 * Pods Hub can reuse the page against the unscoped endpoint.
 */
const PodsUserDetail = ({ fetcher = fetchCompanyPodsUserDetail, backPath, backLabel }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');

  const [loader, setLoader] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!userId) {
      navigate(backPath, { replace: true });
      return;
    }
    let mounted = true;
    setLoader(true);
    fetcher(userId).then((data) => {
      if (!mounted) return;
      setDetail(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fetcher]);

  const user = detail?.user;
  const topPods = detail?.topPods || [];
  const history = detail?.podHistory || [];

  const topOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: topPods.map((entry, index) => paletteAt(index)),
    plotOptions: {
      bar: { horizontal: true, borderRadius: 4, barHeight: '60%', distributed: true }
    },
    dataLabels: { enabled: true, style: { fontSize: '12px' } },
    legend: { show: false },
    grid: CHART_GRID_NO_X,
    xaxis: { categories: topPods.map((entry) => entry.pod) },
    tooltip: { y: { formatter: (val) => `${val} plays` } }
  };

  return (
    <div>
      {loader ? <Loader /> : null}

      <BackLink label={backLabel} onClick={() => navigate(backPath)} />

      {user && (
        <EntityHeader
          className="mt-4"
          image={user.user_profile}
          name={user.name}
          subtitle={user.email}
          action={
            <Chip className={toneChip('brand')}>
              {user.accountType === 'B2B' ? `B2B — ${user.company}` : 'B2C'}
            </Chip>
          }
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5 gap-4 mt-6">
        <StatCard
          icon={STAT_ICONS.plays}
          title="Total Plays"
          value={user ? user.totalPlays : '-'}
        />
        <StatCard
          icon={STAT_ICONS.time}
          title="Listening Time"
          value={user ? minsToHhMm(user.totalListenTimeMins) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.audioLibrary}
          title="Pods Played"
          value={user ? user.podsPlayed : '-'}
        />
        <StatCard
          icon={STAT_ICONS.sessions}
          title="Avg Session"
          value={user ? `${user.avgSessionMins} min` : '-'}
        />
        <StatCard icon={STAT_ICONS.catalog} title="Top Pod" value={detail?.mostPlayedPod || '-'} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Top Pods" subtitle="This user's most played">
          {topPods.length > 0 ? (
            <Chart
              type="bar"
              height={320}
              width="100%"
              series={[{ name: 'Plays', data: topPods.map((entry) => entry.plays) }]}
              options={topOptions}
            />
          ) : (
            <PanelEmpty>No pod activity yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Listening History" subtitle="Most recent pods played">
          {history.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Date</th>
                  <th className="py-2 pr-4 font-medium">Pod</th>
                  <th className="py-2 pr-4 font-medium">Format</th>
                  <th className="py-2 pr-4 font-medium text-right">Plays</th>
                  <th className="py-2 font-medium text-right">Listened</th>
                </>
              }
            >
              {history.map((entry, index) => (
                <tr key={`${entry.date}-${index}`} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{entry.date}</td>
                  <td className="py-2 pr-4">{entry.pod}</td>
                  <td className="py-2 pr-4">
                    {entry.format ? (
                      <Chip className={formatChipClass(entry.format)}>{entry.format}</Chip>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="py-2 pr-4 text-right">{entry.plays}</td>
                  <td className="py-2 text-right">{minsToHhMm(entry.listenMins)}</td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>No listening history yet.</PanelEmpty>
          )}
        </Panel>
      </div>
    </div>
  );
};

PodsUserDetail.propTypes = {
  fetcher: PropTypes.func,
  backPath: PropTypes.string.isRequired,
  backLabel: PropTypes.string
};

PodsUserDetail.defaultProps = {
  backLabel: 'Back'
};

export default PodsUserDetail;
